import { z } from "zod";

import type { MembershipSeatType } from "./memberships";
import type { ModelId } from "./shared/model_id";
import type { RoleType } from "./user";
import { isRoleType } from "./user";

/**
 * system group: Accessible by no-one other than our system API keys. Has access
 * to the system Space which holds the connected data sources.
 *
 * global group: Contains all users from the workspace. Has access to the global
 * Space which holds all existing datasource created before spaces.
 *
 * "regular" groups are groups for which users are selected one by one (as opposed
 * to provisioned groups whose membership is synced from an external identity
 * provider). They come in two flavors depending on how the group was created:
 *
 * regular_auto group: Created implicitly by Azimut (e.g. agent editors, space
 * members).
 *
 * regular_manual group: Created manually by the user via the UI. They can be used
 * to grant specific permissions to users.
 *
 *  provisioned group: Contains all users from a provisioned group.
 */
export const GROUP_KINDS = [
  "regular_auto",
  "regular_manual",
  "global",
  "system",
  "provisioned",
] as const;
export type GroupKind = (typeof GROUP_KINDS)[number];

// Group kinds that can carry a per-group usage spend limit and be surfaced in
// the Usage > Groups admin table. Only "provisioned" (SSO/SCIM directory)
// and regular_manual groups.
export const CAP_ELIGIBLE_GROUP_KINDS = [
  "provisioned",
  "regular_manual",
] as const;

export function isCapEligibleGroupKind(kind: GroupKind): boolean {
  return CAP_ELIGIBLE_GROUP_KINDS.some((k) => k === kind);
}

// Group kinds that represent user-managed membership collections and are
// surfaced in workspace admin UIs (Groups tab, governance). Excludes internal
// system/permission groups.
export const MANAGEABLE_GROUP_KINDS = [
  "provisioned",
  "regular_manual",
] as const;

export type ManageableGroupKind = (typeof MANAGEABLE_GROUP_KINDS)[number];

export function isManageableGroupKind(
  kind: GroupKind
): kind is ManageableGroupKind {
  return MANAGEABLE_GROUP_KINDS.some((k) => k === kind);
}

// Group kinds that any workspace member may see directly (e.g. in the workspace
// Groups listing or when referencing a group by id). Internal kinds
// (`regular_auto`, `system`) are never surfaced this way: they are
// implementation details of spaces and permissions.
export const USER_VISIBLE_GROUP_KINDS = [
  ...MANAGEABLE_GROUP_KINDS,
  "global",
] as const;

export function isUserVisibleGroupKind(
  kind: GroupKind
): kind is UserVisibleGroupKind {
  return USER_VISIBLE_GROUP_KINDS.some((k) => k === kind);
}

export type UserVisibleGroupKind = (typeof USER_VISIBLE_GROUP_KINDS)[number];

// A group can grant a workspace role to its active members. Only "admin" and
// "manager" are grantable: every member is at least a "user". A group with a
// null `grantedRole` grants no role. When a user
// belongs to several role-granting groups, the highest role wins
// (admin > manager). See `GroupResource.computeUserRoleFromGroups`.
export const GROUP_GRANTABLE_ROLES = ["admin", "manager"] as const;
export type GroupGrantableRole = (typeof GROUP_GRANTABLE_ROLES)[number];

// A group grants a billable seat *tier* to its active members — `workspace`,
// `pro` or `max`. Only the base (monthly) variants are grantable: cadence is not
// chosen per group. When a member is already on the granted tier the sync keeps
// their existing cadence (so a manual yearly upgrade is preserved); otherwise
// they get the monthly seat (or the yearly one if the contract bills only that).
// `free`/`none` are not grantable, and a null `grantedSeatType` grants no seat.
// When a user is in several seat-granting groups the highest tier wins (per
// `SEAT_TYPE_ORDER`). See `GroupResource.computeUserSeatFromGroups`.
export const GROUP_GRANTABLE_SEAT_TYPES = [
  "workspace",
  "pro",
  "max",
] as const satisfies readonly MembershipSeatType[];
export type GroupGrantableSeatType =
  (typeof GROUP_GRANTABLE_SEAT_TYPES)[number];

export function isGroupGrantableSeatType(
  value: unknown
): value is GroupGrantableSeatType {
  return GROUP_GRANTABLE_SEAT_TYPES.some((seatType) => seatType === value);
}

export function isSystemGroupKind(value: GroupKind): boolean {
  return value === "system";
}
export function isGlobalGroupKind(value: GroupKind): boolean {
  return value === "global";
}

export function isRegularManualGroupKind(value: GroupKind): boolean {
  return value === "regular_manual";
}

// A group reduced to what a client needs to display it, without model ids.
export type LightGroupType = {
  id: string;
  name: string;
};

export type GroupType = {
  id: ModelId;
  name: string;
  sId: string;
  kind: GroupKind;
  workspaceId: ModelId;
  memberCount: number;
  // Per-group usage spend limit (excluding seat allowance), applied per member.
  // null means the group carries no cap (falls back to the workspace default).
  poolCapAwuCredits: number | null;
  // Workspace role granted to this group's active members (admin or manager),
  // or null when the group grants no role.
  grantedRole: GroupGrantableRole | null;
  // Billable seat type granted to this group's active members — a full paid seat
  // type including cadence (e.g. `pro` or `pro_yearly`), or null when the group
  // grants no seat.
  grantedSeatType: GroupGrantableSeatType | null;
  // Member sIds, only populated when explicitly requested
  memberIds?: string[];
};

export const GroupKindCodec = z.enum([
  "global",
  "regular_auto",
  "regular_manual",
  "system",
  "provisioned",
]);

const DustGroupIdsHeader = "X-Dust-Group-Ids";

export function getGroupIdsFromHeaders(
  headers: Record<string, string | string[] | undefined>
): string[] | undefined {
  const groupIds = headers[DustGroupIdsHeader.toLowerCase()];
  if (typeof groupIds === "string" && groupIds.trim().length > 0) {
    return groupIds
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
  } else {
    return undefined;
  }
}

const DustRoleHeader = "X-Dust-Role";

export function getRoleFromHeaders(
  headers: Record<string, string | string[] | undefined>
): RoleType | undefined {
  let role = headers[DustRoleHeader.toLowerCase()];
  if (typeof role === "string") {
    role = role.trim();
    if (role.length > 0 && isRoleType(role)) {
      return role;
    }
  }
  return undefined;
}

/**
 * Pass the user's role to the API via headers for internal system-key calls (e.g., runApp).
 */
export function getHeaderFromRole(role: RoleType | undefined) {
  if (!role) {
    return undefined;
  }
  return {
    [DustRoleHeader]: role,
  };
}

export function getHeadersFromRequestedGroupIds(
  groupIds: string[],
  role: RoleType
): Record<string, string> | undefined {
  if (groupIds.length === 0) {
    return undefined;
  }
  return {
    [DustGroupIdsHeader]: groupIds.join(","),
    [DustRoleHeader]: role,
  };
}

export const SPACE_GROUP_PREFIX = "Group for space";
export const PROJECT_GROUP_PREFIX = "Group for Pod";
export const PROJECT_EDITOR_GROUP_PREFIX = "Editors for Pod";
export const GLOBAL_SPACE_NAME = "Company Data";
